import React, { useMemo, useCallback, useReducer } from 'react';

import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
  buildAuthorizedPutRequest,
} from '../../http/request-templates';
import { UsersContext } from './UsersContext';
import usersReducer, {
  ERR_ADMIN_GET_PAYER,
  RCV_ADMIN_GET_PAYER,
  REQ_ADMIN_GET_PAYER,
  REQ_CLIENT_GET_USERS,
  RCV_CLIENT_GET_USERS,
  ERR_CLIENT_GET_USERS,
  REQ_CLIENT_GET_USER,
  RCV_CLIENT_GET_USER,
  ERR_CLIENT_GET_USER,
  REQ_CLIENT_UPDATE_USER,
  RCV_CLIENT_UPDATE_USER,
  ERR_CLIENT_UPDATE_USER,
  REQ_ADMIN_GET_USER,
  RCV_ADMIN_GET_USER,
  ERR_ADMIN_GET_USER,
  REQ_ADMIN_CREATE_CLIENT_ADMIN,
  RCV_ADMIN_CREATE_CLIENT_ADMIN,
  ERR_ADMIN_CREATE_CLIENT_ADMIN,
  REQ_ADMIN_UPDATE_CLIENT_ADMIN,
  RCV_ADMIN_UPDATE_CLIENT_ADMIN,
  ERR_ADMIN_UPDATE_CLIENT_ADMIN,
  REQ_ADMIN_REMOVE_USER,
  RCV_ADMIN_REMOVE_USER,
  ERR_ADMIN_REMOVE_USER,
  REQ_ADMIN_ENABLE_DISABLE,
  RCV_ADMIN_ENABLE_DISABLE,
  ERR_ADMIN_ENABLE_DISABLE,
  CLEAR_USER,
  usersReducerInitialState,
  REQ_CLIENT_CREATE_USER,
  RCV_CLIENT_CREATE_USER,
  ERR_CLIENT_CREATE_USER,
  REQ_CLIENT_RESET_PASSWORD,
  RCV_CLIENT_RESET_PASSWORD,
  ERR_CLIENT_RESET_PASSWORD,
  REQ_CLIENT_ENABLE_DISABLE,
  RCV_CLIENT_ENABLE_DISABLE,
  ERR_CLIENT_ENABLE_DISABLE,
  REQ_ADMIN_SEARCH,
  RCV_ADMIN_SEARCH,
  ERR_ADMIN_SEARCH,
} from './usersReducer';
import useAuth from '../Auth/useAuth';
import {
  ClientCreateUser,
  CreateUser,
  Payer,
  PayerUser,
  User,
  AdminSearchResult,
} from './UsersModels';
import useHttp from '../../http/useHttp';
import { useTranslation } from 'react-i18next';

interface UsersProviderProps {
  children: React.ReactNode;
}

const UsersProvider = ({ children }: UsersProviderProps) => {
  const { getAccessToken } = useAuth();
  const { httpRawJson } = useHttp();
  const { t } = useTranslation();

  const [usersState, dispatch] = useReducer(usersReducer, usersReducerInitialState);

  const adminGetPayer = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: REQ_ADMIN_GET_PAYER });
        const response = await httpRawJson<Payer>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/payers/${id}/users`,
          buildAuthorizedGetRequest(await getAccessToken())
        );
        dispatch({ type: RCV_ADMIN_GET_PAYER, payer: { ...response, customer_number: id } });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_GET_PAYER });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const clientGetUsers = useCallback(async () => {
    try {
      dispatch({ type: REQ_CLIENT_GET_USERS });
      const response = await httpRawJson<PayerUser[]>(
        `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users`,
        buildAuthorizedGetRequest(await getAccessToken())
      );
      dispatch({ type: RCV_CLIENT_GET_USERS, users: response });
    } catch (e) {
      dispatch({ type: ERR_CLIENT_GET_USERS });
    }
  }, [getAccessToken, httpRawJson]);

  const clientGetUser = useCallback(
    async (email: string) => {
      try {
        dispatch({ type: REQ_CLIENT_GET_USER });
        const response = await httpRawJson<User>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users/${email}`,
          buildAuthorizedGetRequest(await getAccessToken())
        );
        dispatch({ type: RCV_CLIENT_GET_USER, user: response });
      } catch (e) {
        dispatch({ type: ERR_CLIENT_GET_USER });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const clientResetPassword = useCallback(
    async (email: string) => {
      try {
        dispatch({ type: REQ_CLIENT_RESET_PASSWORD });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users/${email}/reset-password`,
          buildAuthorizedPutRequest(await getAccessToken()),
          {
            snackbarOptions: {
              onError: <>{t('users_reset_password_error', { email })}</>,
              onSuccess: <>{t('users_reset_password_success', { email })}</>,
            },
          }
        );
        dispatch({ type: RCV_CLIENT_RESET_PASSWORD });
      } catch (e) {
        dispatch({ type: ERR_CLIENT_RESET_PASSWORD });
      }
    },
    [getAccessToken, httpRawJson, t]
  );

  const clientEnableDisableUser = useCallback(
    async (email: string, enable: boolean) => {
      try {
        dispatch({ type: REQ_CLIENT_ENABLE_DISABLE });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users/${email}/${
            enable ? 'enable' : 'disable'
          }`,
          buildAuthorizedPutRequest(await getAccessToken()),
          {
            snackbarOptions: {
              onError: <>{t(enable ? 'users_enable_error' : 'users_disable_error', { email })}</>,
              onSuccess: (
                <>{t(enable ? 'users_enable_success' : 'users_disable_success', { email })}</>
              ),
            },
          }
        );
        dispatch({ type: RCV_CLIENT_ENABLE_DISABLE, email, enable });
      } catch (e) {
        dispatch({ type: ERR_CLIENT_ENABLE_DISABLE });
      }
    },
    [getAccessToken, httpRawJson, t]
  );

  const adminGetUser = useCallback(
    async (id: string, email: string) => {
      try {
        dispatch({ type: REQ_ADMIN_GET_USER });
        const response = await httpRawJson<User>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/payers/${id}/users/${email}`,
          buildAuthorizedGetRequest(await getAccessToken())
        );
        dispatch({ type: RCV_ADMIN_GET_USER, user: response });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_GET_USER });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const adminCreateUser = useCallback(
    async (id: string, payload: CreateUser) => {
      try {
        dispatch({ type: REQ_ADMIN_CREATE_CLIENT_ADMIN });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/payers/${id}/users`,
          buildAuthorizedPostRequest(await getAccessToken(), payload)
        );
        dispatch({ type: RCV_ADMIN_CREATE_CLIENT_ADMIN, user: payload });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_CREATE_CLIENT_ADMIN });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const clientCreateUser = useCallback(
    async (payload: ClientCreateUser) => {
      try {
        dispatch({ type: REQ_CLIENT_CREATE_USER });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users`,
          buildAuthorizedPostRequest(await getAccessToken(), payload)
        );
        dispatch({ type: RCV_CLIENT_CREATE_USER, user: payload });
      } catch (e) {
        dispatch({ type: ERR_CLIENT_CREATE_USER });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const adminUpdateUser = useCallback(
    async (payload: CreateUser) => {
      try {
        dispatch({ type: REQ_ADMIN_UPDATE_CLIENT_ADMIN });
        await httpRawJson<undefined>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/users/${payload.email}`,
          buildAuthorizedPutRequest(await getAccessToken(), payload)
        );
        dispatch({ type: RCV_ADMIN_UPDATE_CLIENT_ADMIN, user: payload });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_UPDATE_CLIENT_ADMIN });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const clientUpdateUser = useCallback(
    async (payload: ClientCreateUser) => {
      try {
        dispatch({ type: REQ_CLIENT_UPDATE_USER });
        await httpRawJson<undefined>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users/${payload.email}`,
          buildAuthorizedPutRequest(await getAccessToken(), payload)
        );

        const user = await httpRawJson<User>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/client/users/${payload.email}`,
          buildAuthorizedGetRequest(await getAccessToken())
        );

        dispatch({ type: RCV_CLIENT_UPDATE_USER, user });
      } catch (e) {
        dispatch({ type: ERR_CLIENT_UPDATE_USER });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const adminRemoveUser = useCallback(
    async (id: string, email: string) => {
      try {
        dispatch({ type: REQ_ADMIN_REMOVE_USER });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/users/${email}`,
          buildAuthorizedDeleteRequest(await getAccessToken()),
          {
            snackbarOptions: {
              onError: <>{t('users_remove_user_error', { email })}</>,
              onSuccess: <>{t('users_remove_user_success', { email })}</>,
            },
          }
        );
        dispatch({ type: RCV_ADMIN_REMOVE_USER, email });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_REMOVE_USER });
      }
    },
    [getAccessToken, httpRawJson, t]
  );

  const adminEnableDisableUser = useCallback(
    async (email: string, enable: boolean) => {
      try {
        dispatch({ type: REQ_ADMIN_ENABLE_DISABLE });
        await httpRawJson(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/users/${email}/${
            enable ? 'enable' : 'disable'
          }`,
          buildAuthorizedPutRequest(await getAccessToken()),
          {
            snackbarOptions: {
              onError: <>{t(enable ? 'users_enable_error' : 'users_disable_error', { email })}</>,
              onSuccess: (
                <>{t(enable ? 'users_enable_success' : 'users_disable_success', { email })}</>
              ),
            },
          }
        );
        dispatch({ type: RCV_ADMIN_ENABLE_DISABLE, email, enable });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_ENABLE_DISABLE });
      }
    },
    [getAccessToken, httpRawJson, t]
  );

  const adminSearch = useCallback(
    async (query: string) => {
      try {
        dispatch({ type: REQ_ADMIN_SEARCH });
        const encodedQuery = encodeURIComponent(query.trim());
        const response = await httpRawJson<AdminSearchResult[]>(
          `${
            import.meta.env.VITE_CUSTOMER_PORTAL_API
          }/admin/admin-users/search?search=${encodedQuery}`,
          buildAuthorizedGetRequest(await getAccessToken())
        );
        dispatch({ type: RCV_ADMIN_SEARCH, adminSearchResult: response });
      } catch (e) {
        dispatch({ type: ERR_ADMIN_SEARCH });
      }
    },
    [getAccessToken, httpRawJson]
  );

  const clearUser = useCallback(() => dispatch({ type: CLEAR_USER }), []);

  const value = useMemo(
    () => ({
      ...usersState,
      adminGetPayer,
      adminGetUser,
      adminCreateUser,
      adminUpdateUser,
      adminRemoveUser,
      adminEnableDisableUser,
      clearUser,
      clientGetUsers,
      clientGetUser,
      clientCreateUser,
      clientUpdateUser,
      clientResetPassword,
      clientEnableDisableUser,
      adminSearch,
    }),
    [
      adminCreateUser,
      adminEnableDisableUser,
      adminGetPayer,
      adminGetUser,
      adminRemoveUser,
      adminSearch,
      adminUpdateUser,
      clearUser,
      clientCreateUser,
      clientEnableDisableUser,
      clientGetUser,
      clientGetUsers,
      clientResetPassword,
      clientUpdateUser,
      usersState,
    ]
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
};

export default UsersProvider;
